import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";

import TruncateText from "../../../components/TruncateText";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Button from "../../../components/Button";
import messages from "../../../assets/locale/messages";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import noCategoryImg from "../../../assets/images/placeholders/noCategoryImagePlaceholder.svg";
import { approvedCategoriesListRequest } from "../../../store/Categories/actions";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { ListDeleteIcon, ListEditIcon } from "../../../utils/Icons";
import { hasPermission, PERMISSIONS_IDS } from "../../../utils/Roles";
import DeleteCategory from "../DeleteCategory";
import "../Categories.scss";

const ApprovedCategories = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { categories, shared } = messages[lang];
  const categoriesList = useSelector(
    (state) => state.categories.approvedCategories.list
  );
  const categoriesMeta = useSelector(
    (state) => state.categories.approvedCategories.meta
  );
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [deletedCategoryDetails, setDeletedCategoryDetails] =
    useState({});
  const [deleteCategoryModalOpen, setDeleteCategoryModalOpen] =
    useState(false);

  const handleAddCategory = () => {
    navigate(ROUTE_PATHS.addCategory);
  };
  const handleEditCategory = (id) => {
    navigate(ROUTE_PATHS.editCategory.replace(":id", id));
  };

  const handleDeleteCategory = (name, id) => {
    setDeletedCategoryDetails({
      id,
      name,
      src: ROUTE_PATHS.approvedCategories,
      params: {
        page,
        search_value: searchValue
      }
    });
    setDeleteCategoryModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteCategoryModalOpen(false);
    setDeletedCategoryDetails({});
  };

  const requestCategoriesList = () => {
    dispatch(
      approvedCategoriesListRequest({
        params: {
          page,
          search_value: searchValue
        }
      })
    );
  };

  useEffect(() => {
    requestCategoriesList();
  }, [page, searchValue]);

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const renderCategoriesTableRow = ({
    Id,
    Name,
    Description,
    AdminCoverImage,
    OriginalAdminCoverImageName,
    SubCategoriesCount
  }) => (
    <TableRow
      className="pointer"
      key={`request-row-${Id}`}
      onClick={() => {
        navigate(
          ROUTE_PATHS.categoryDetailsApproved.replace(":id", Id)
        );
      }}>
      <TableCell>
        <div className="d-flex align-items-center gap-3">
          <img
            src={AdminCoverImage || noRequestImg}
            onError={onImageError}
            alt={OriginalAdminCoverImageName}
            className="category-img faint-grey-border border-radius-8"
          />
          <TruncateText text={Name || "__"} />
        </div>
      </TableCell>
      <TableCell>
        <TruncateText
          showTooltip={false}
          text={Description || "__"}
        />
      </TableCell>
      <TableCell>
        {`${
          SubCategoriesCount ?? "0"
        } ${categories.list.tableHeader.subCategories}`}
      </TableCell>
      {(hasPermission(PERMISSIONS_IDS.category_edit) ||
        hasPermission(PERMISSIONS_IDS.category_delete)) && (
        <TableCell>
          <div className="w-100 d-flex justify-content-center gap-2">
            {hasPermission(PERMISSIONS_IDS.category_edit) && (
              <ListEditIcon
                className={"list-action-icon pointer"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCategory(Id);
                }}
              />
            )}
            {hasPermission(PERMISSIONS_IDS.category_delete) && (
              <ListDeleteIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCategory(Name, Id);
                }}
                className={"list-delete-action-icon pointer"}
              />
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <div className="categories-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-md-center align-items-start">
        <div className="title-count-container d-md-flex justify-content-md-center justify-content-start align-items-center">
          <div>
            <Typography variant="h5" className="title-color me-1">
              {categories.list.categoriesList}
            </Typography>
          </div>

          <div className="block-title text-secondary">
            <Typography>{`(${categoriesMeta?.count ? categoriesMeta?.count : "0"} ${categoriesMeta?.count === 1 ? categories.list.category : categories.list.categories})`}</Typography>
          </div>
        </div>
        {hasPermission(PERMISSIONS_IDS.category_add) && (
          <div className="title-buttons-container d-flex justify-content-md-center justify-content-end mt-2 mt-md-0">
            <Button
              label={
                <span className="d-flex align-items-center">
                  <Add fontSize="medium" />
                  <span className="ps-1 d-none d-md-block">
                    {categories.list.addMoreCategories}
                  </span>
                </span>
              }
              onClick={handleAddCategory}
              customSpacing={"px-2 px-md-4"}
            />
          </div>
        )}
      </div>
      <div className="categories-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 border-radius-8">
        <div className="table-header d-flex px-4 align-items-center w-100 mb-4">
          <SearchInput
            name="CategorySearch"
            id="CategorySearch"
            value={searchValue}
            inputClass="border-radius-8"
            inputWrapperClass=" search-input-wrapper search-input-wrapper-width"
            placeholder={categories.list.searchPlaceholder}
            onChange={(value) => {
              setPage(1);
              setSearchValue(value);
            }}
          />
        </div>
        <div className="table-content">
          <div>
            {categoriesList?.length ? (
              <Table
                className={"px-0"}
                headlines={[
                  categories.list.tableHeader.category,
                  categories.list.tableHeader.description,
                  categories.list.tableHeader.subCategories,
                  (hasPermission(PERMISSIONS_IDS.category_edit) ||
                    hasPermission(PERMISSIONS_IDS.category_delete)) &&
                    shared.actions
                ]}
                rows={
                  !isLoading &&
                  categoriesList &&
                  categoriesList.map((category) =>
                    renderCategoriesTableRow(category)
                  )
                }
              />
            ) : (
              <div className="border-top">
                {!isLoading && (
                  <EmptyState
                    subTitle={
                      !!searchValue && !categoriesList.length
                        ? categories.list.noSearchResults
                        : categories.list.noCategoriesFound
                    }
                    showActionButton={
                      !searchValue && categoriesList.length === 0
                    }
                    ButtonLabel={
                      <span className="d-flex align-items-center">
                        <Add fontSize="medium" />
                        <span className="ps-1 ">
                          {categories.list.addMoreCategories}
                        </span>
                      </span>
                    }
                    handleCreate={handleAddCategory}
                    buttonClassName="empty-state-create-button border-radius-8 w-auto"
                    imgSrc={noCategoryImg}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
          {!isLoading && categoriesList.length > 0 && (
            <Pagination
              count={categoriesMeta?.pages}
              page={page || categoriesMeta.currentPage}
              handleChange={(page) => {
                setPage(page);
              }}
              defaultPage={1}
            />
          )}
        </div>
      </div>
      {deleteCategoryModalOpen && (
        <DeleteCategory
          open={deleteCategoryModalOpen}
          closeModal={handleCloseModal}
          categoryInfo={deletedCategoryDetails}
        />
      )}
    </div>
  );
};

export default ApprovedCategories;
